import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { StatCard } from '@/components/StatCard'
import { useOverview } from '@/features/platformAdmin/queries'
import { AuditTable } from './_AuditTable'

function formatMoneyCents(cents: number, currency: string): string {
  const amount = cents / 100
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

export function OverviewTab() {
  const q = useOverview()

  if (q.isLoading) {
    return (
      <Card>
        <CardContent className="py-10">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  if (q.isError || !q.data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load overview.</AlertDescription>
      </Alert>
    )
  }

  const c = q.data.counts
  const breakdown = q.data.subscriptionBreakdown ?? []
  const recent = q.data.recentAudit ?? []
  const refundsSummary = q.data.refundsSummary
  const refundsTopRow = refundsSummary?.byCurrency[0]
  const refundsTotal = refundsSummary?.byCurrency.reduce((sum, r) => sum + r.grossRefundedCents, 0) ?? 0
  const refundsLabel = refundsTopRow
    ? formatMoneyCents(refundsTotal, refundsTopRow.currency)
    : '-'
  const refundsCount = refundsSummary?.byCurrency.reduce((sum, r) => sum + r.refundCount, 0) ?? 0

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Customers &amp; Tenants
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total customers" value={c.totalUsers} />
          <StatCard label="Total tenants" value={c.totalTenants} />
          <StatCard label="Active tenants" value={c.activeTenants} />
          <StatCard label="Suspended tenants" value={c.suspendedTenants} />
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Activity &amp; Usage
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard label="Signups (7d)" value={c.usersLast7d} />
          <StatCard label="Signups (30d)" value={c.usersLast30d} />
          <StatCard label="Pending invitations" value={c.pendingInvitations} />
          <StatCard label="Tokens used" value={c.totalTokens.toLocaleString()} />
          <StatCard label="Transactions processed" value={c.totalTransactions.toLocaleString()} />
          <StatCard label="Bokapsys staff" value={c.staffMemberships} />
          <StatCard
            label="Managed refunds (30d)"
            value={refundsLabel}
            hint={refundsCount > 0 ? `${refundsCount} refund${refundsCount === 1 ? '' : 's'}` : 'no refunds'}
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Subscription breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {breakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground">No subscription data.</p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {breakdown.map((s) => (
                <li key={s.tier} className="flex items-center justify-between py-2">
                  <span className="font-medium">{s.tier}</span>
                  <span className="font-mono text-muted-foreground">{s.n}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent activity</CardTitle>
        </CardHeader>
        <CardContent>
          <AuditTable rows={recent} />
        </CardContent>
      </Card>
    </div>
  )
}
